// Business units backing the header switcher and the Cross-BU Comparison page.
//
// Live source: GET /rest/api/v1/business-units/mids, which returns
//   { total, defaultMid, enterpriseName, enterpriseId, mids: [{ mid, name }] }
// X-Token / X-Session-Id are attached by the axios interceptor.
//
// The normalized list is cached in localStorage under `userBusinessUnits` so the
// header can paint the previous selection synchronously on the next load instead
// of flashing an empty switcher while the request is in flight. When there is no
// cache yet (first visit), the static prototype fixture stands in until the
// request resolves.

import axios from 'axios';
import { BUSINESS_UNITS } from '../data/monitoringData';

const MIDS_ENDPOINT = '/rest/api/v1/business-units/mids';
const STORAGE_KEY   = 'userBusinessUnits';
const SELECTED_KEY  = 'ahSelectedBuId';

// Fired on window after a successful fetch so every mounted switcher refreshes.
export const BU_EVENT = 'ah:business-units';

// Fired when the working BU changes. The header switcher and the Cross-BU table
// are mounted at the same time, so both have to follow a selection made in the other.
export const BU_SELECTED_EVENT = 'ah:business-unit-selected';

// The backend labels the enterprise BU "BU <mid>", which renders as
// "BU 514014416 — MID 514014416" in the header. Prefer the account name there.
const GENERIC_NAME = /^BU\s+\d+$/i;

export function normalizeBusinessUnits(payload) {
    const rawMids = Array.isArray(payload && payload.mids) ? payload.mids : [];
    const defaultMid = payload && payload.defaultMid != null ? String(payload.defaultMid) : '';
    const enterprise = payload && payload.enterpriseName ? String(payload.enterpriseName) : '';

    const units = rawMids
        .filter(m => m && (m.mid != null || m.name))
        .map(m => {
            const mid       = m.mid != null ? String(m.mid) : '';
            const isParent  = !!mid && mid === defaultMid;
            const rawName   = m.name != null ? String(m.name) : '';
            const useEnterprise = isParent && enterprise && (!rawName || GENERIC_NAME.test(rawName));
            return {
                id:     mid || rawName,
                name:   useEnterprise ? enterprise : (rawName || `MID ${mid}`),
                mid,
                parent: isParent,
            };
        });

    // Parent (default) BU first — the header and the comparison table both show it on top.
    return [...units.filter(u => u.parent), ...units.filter(u => !u.parent)];
}

export async function fetchBusinessUnits() {
    const { data } = await axios.get(MIDS_ENDPOINT);
    const units = normalizeBusinessUnits(data);
    if (!units.length) return units;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(units));

    // Drop a selection that no longer exists (fixture id, or a BU from a previous
    // account) so getSelectedBusinessUnit() falls back to the parent BU.
    const saved = localStorage.getItem(SELECTED_KEY);
    if (saved && !units.some(u => u.id === saved)) {
        localStorage.removeItem(SELECTED_KEY);
    }

    window.dispatchEvent(new CustomEvent(BU_EVENT, { detail: { units } }));
    return units;
}

export function getAllBusinessUnits() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed) && parsed.length) {
                return parsed
                    .filter(u => u && (u.name || u.mid))
                    .map(u => ({
                        id:     String(u.id || u.mid || u.name),
                        name:   String(u.name || `MID ${u.mid}`),
                        mid:    String(u.mid || ''),
                        parent: u.parent === true,
                    }));
            }
        }
    } catch (e) {
        // Malformed payload — fall through to the static fixture.
    }

    return BUSINESS_UNITS;
}

export function getSelectedBusinessUnit() {
    const units = getAllBusinessUnits();
    if (!units.length) return null;
    const saved = localStorage.getItem(SELECTED_KEY);
    return units.find(u => u.id === saved) || units[0];
}

export function setSelectedBusinessUnit(id) {
    localStorage.setItem(SELECTED_KEY, String(id));
    window.dispatchEvent(new CustomEvent(BU_SELECTED_EVENT, {
        detail: { unit: getSelectedBusinessUnit() },
    }));
}

// The MID that page requests should be scoped to, or '' when they should go out
// unscoped. Empty for the default (enterprise) BU — the backend already resolves
// that from the connection — and empty while the list is still the static
// fixture, so a prototype MID is never sent to a real account.
export function getSelectedMid() {
    if (!localStorage.getItem(STORAGE_KEY)) return '';
    const unit = getSelectedBusinessUnit();
    if (!unit || unit.parent) return '';
    return unit.mid || '';
}

export function filterBusinessUnits(units, query) {
    const q = (query || '').trim().toLowerCase();
    if (!q) return units;
    return units.filter(u =>
        u.name.toLowerCase().includes(q) || (u.mid && u.mid.includes(q))
    );
}
